/**
 * Session Interface Component
 * Displays flashcard questions and handles answer submission
 */
import React, { useState, useEffect } from 'react';
import APIService from '../services/APIService';
import VoiceRecorder from './VoiceRecorder';
import FeedbackDisplay from './FeedbackDisplay';

const SessionInterface = ({ sessionData, onSessionComplete, onCancel }) => {
  const [question, setQuestion] = useState(null);
  const [textAnswer, setTextAnswer] = useState('');
  const [confidence, setConfidence] = useState(5);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [showHints, setShowHints] = useState(false);

  useEffect(() => {
    // Load the first question
    loadCurrentQuestion();
  }, [sessionData.session_id]);

  const loadCurrentQuestion = async () => {
    try {
      setLoading(true);
      setError(null);
      setFeedback(null);
      setTextAnswer('');
      setShowHints(false);
      setQuestionStartTime(Date.now());

      const q = await APIService.getCurrentQuestion(sessionData.session_id);
      setQuestion(q);
    } catch (err) {
      setError(`Failed to load question: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async (answerText, answerType = 'text', selfScore = null, selfFeedback = null) => {
    if (sessionData.no_evaluation && selfScore === null) {
      setError('Please select a self-assessment');
      return;
    }

    if (!sessionData.no_evaluation && (!answerText || !answerText.trim())) {
      setError('Answer cannot be empty');
      return;
    }

    if (!question) {
      setError('No question loaded');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Calculate time taken for this question
      const timeTaken = (Date.now() - questionStartTime) / 1000;

      // Submit answer and get evaluation or record self-score
      const options = {};
      if (sessionData.no_evaluation) {
        options.self_score = selfScore;
        options.self_feedback = selfFeedback || '';
      }

      const feedbackData = await APIService.submitAnswer(
        sessionData.session_id,
        question.question_id,
        sessionData.no_evaluation ? '' : answerText,
        sessionData.no_evaluation ? 'text' : answerType,
        confidence,
        options
      );

      setFeedback({
        ...feedbackData,
        timeTaken,
      });
    } catch (err) {
      setError(`Failed to submit answer: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    handleSubmitAnswer(textAnswer, 'text');
  };

  const handleVoiceTranscribed = (transcript) => {
    setShowVoiceRecorder(false);
    handleSubmitAnswer(transcript, 'voice');
  };

  const handleContinue = async () => {
    // reset feedback and load next
    setFeedback(null);
    // Check if this was the last question
    if (question.question_index >= question.total_questions) {
      // Session is complete
      try {
        const report = await APIService.getSessionReport(sessionData.session_id);
        onSessionComplete(report);
      } catch (err) {
        setError(`Failed to get session report: ${err.message}`);
      }
    } else {
      // Load next question
      await loadCurrentQuestion();
    }
  };

  if (loading && !question) {
    return <div className="loading">Loading question...</div>;
  }

  // in flashcard mode, clicking question reveals answer via feedback state
  const handleRevealFlashcard = async () => {
    if (question) {
      // Submit answer to backend first (with empty answer for flashcard)
      try {
        setSubmitting(true);
        setError(null);
        
        const feedbackData = await APIService.submitAnswer(
          sessionData.session_id,
          question.question_id,
          '',
          'text',
          5,
          {}
        );

        // Show the revealed answer
        setFeedback(feedbackData);
      } catch (err) {
        setError(`Failed to reveal answer: ${err.message}`);
      } finally {
        setSubmitting(false);
      }
    }
  };

  if (showVoiceRecorder) {
    return (
      <VoiceRecorder
        onTranscriptionReady={handleVoiceTranscribed}
        onCancel={() => setShowVoiceRecorder(false)}
      />
    );
  }

  return (
    <div className="session-interface">
      <div className="session-container">
        {/* Session Header with Progress */}
        <div className="session-header">
          <h1 className="session-title">Learning Session</h1>
          <div className="session-progress">
            <div className="progress-info">
              <div className="progress-count">
                Question {question.question_index} of {question.total_questions}
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${(question.question_index / question.total_questions) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Question Card */}
        <div className="question-card">
          <div className="question-header">
            <div className="question-meta">
              <div className="question-topic">{question.topic}</div>
              <div className="question-concept">{question.concept}</div>
            </div>
            <span className="question-number">Q{question.question_index}</span>
          </div>

          <h2 className="question-text">{question.question}</h2>

          {/* Hints Section */}
          {question.hints && question.hints.length > 0 && (
            <div className="hints-section">
              <button
                className="hints-toggle-btn"
                onClick={() => setShowHints(!showHints)}
                aria-expanded={showHints}
              >
                <span>💡 Hints ({question.hints.length})</span>
                <span className="hints-toggle-icon">▼</span>
              </button>
              {showHints && (
                <div className="hints-list">
                  {question.hints.map((hint, idx) => (
                    <div key={idx} className="hint-item">
                      {hint}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && (
            <div style={{
              padding: 'var(--space-4)',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 'var(--radius-lg)',
              color: 'var(--danger)',
              marginTop: 'var(--space-6)',
              fontSize: 'var(--text-sm)'
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Answer Input Section */}
        <div style={{ marginBottom: 'var(--space-8)' }}>
          {sessionData.flashcard_mode ? (
            <div className="flashcard-container">
              {!feedback ? (
                <button
                  onClick={handleRevealFlashcard}
                  className="flashcard"
                  disabled={submitting}
                >
                  <div className="flashcard-content">
                    <div className="flashcard-label">Click to reveal answer</div>
                    <div className="flashcard-text">
                      {submitting ? '⏳ Loading...' : '✨ Tap Here'}
                    </div>
                  </div>
                </button>
              ) : (
                <div className="flashcard revealed">
                  <div className="flashcard-content">
                    <div className="flashcard-label">Answer</div>
                    <div className="flashcard-text">{feedback.correct_answer}</div>
                  </div>
                </div>
              )}
            </div>
          ) : sessionData.no_evaluation ? (
            <div className="self-assessment">
              <div style={{
                padding: 'var(--space-6)',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
              }}>
                <label className="confidence-label">
                  ✍️ How confident are you with your answer?
                </label>
                <div className="confidence-options">
                  {[
                    { label: '✓ Correct', score: 100 },
                    { label: '~ Partial', score: 50 },
                    { label: '✕ Incorrect', score: 0 },
                    { label: '? Unsure', score: 0 },
                  ].map((option) => (
                    <button
                      key={option.label}
                      className="confidence-option"
                      onClick={() => handleSubmitAnswer('', 'text', option.score, option.label)}
                      disabled={submitting}
                      type="button"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              padding: 'var(--space-8)',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
            }}>
              {/* Text Answer Tab */}
              <div>
                <h3 style={{
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-weight-600)',
                  color: 'var(--text-primary)',
                  marginBottom: 'var(--space-4)',
                }}>
                  ✍️ Type Your Answer
                </h3>

                <form onSubmit={handleTextSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                  <textarea
                    className="answer-input"
                    value={textAnswer}
                    onChange={(e) => setTextAnswer(e.target.value)}
                    placeholder="Type your detailed answer here..."
                    disabled={submitting}
                  />

                  <div className="score-selector">
                    <label htmlFor="confidence" className="score-label">
                      Confidence Level: <strong>{confidence}/10</strong>
                    </label>
                    <input
                      id="confidence"
                      type="range"
                      min="1"
                      max="10"
                      value={confidence}
                      onChange={(e) => setConfidence(parseInt(e.target.value))}
                      disabled={submitting}
                      style={{
                        width: '100%',
                        height: '6px',
                        borderRadius: '3px',
                        background: 'linear-gradient(90deg, var(--primary) 0%, var(--secondary) 100%)',
                        outline: 'none',
                        WebkitAppearance: 'none',
                      }}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={submitting || !textAnswer.trim()}
                    style={{ width: '100%' }}
                  >
                    {submitting ? '⏳ Evaluating...' : '✓ Submit Answer'}
                  </button>
                </form>

                <div style={{
                  margin: 'var(--space-6) 0',
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-600)',
                }}>
                  OR
                </div>

                {/* Voice Answer Section */}
                <div className="voice-input-section">
                  <h3 style={{
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-weight-600)',
                    color: 'var(--text-primary)',
                    marginBottom: 'var(--space-4)',
                  }}>
                    🎤 Voice Answer
                  </h3>
                  <button
                    onClick={() => setShowVoiceRecorder(true)}
                    className="voice-btn"
                    disabled={submitting}
                    type="button"
                  >
                    🎙️ Record Answer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Feedback Display */}
        {feedback && <FeedbackDisplay feedback={feedback} sessionMode={sessionData.flashcard_mode ? 'flashcard' : (sessionData.no_evaluation ? 'self' : 'ai')} />}

        {/* Action Buttons */}
        <div className="session-actions">
          {feedback && (
            <button
              onClick={handleContinue}
              className="btn-primary btn-large"
            >
              {question.question_index >= question.total_questions
                ? '✓ Complete Session'
                : '→ Next Question'}
            </button>
          )}
          <button
            onClick={onCancel}
            className="btn-danger exit-btn"
          >
            ✕ Exit Session
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionInterface;
